import { type V2, closestOnSegment, dist } from '../core/math';
import { Rng, hash2 } from '../core/rng';
import { NavGrid, pointInPolygon } from '../sim/navGrid';
import type { PropInstance, ZoneLayout } from './types';

/** Smooth value noise in [0,1] on an infinite plane (deterministic). */
export function noise2(x: number, z: number, seed = 0): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const xf = x - xi;
  const zf = z - zi;
  const u = xf * xf * (3 - 2 * xf);
  const v = zf * zf * (3 - 2 * zf);
  const a = hash2(xi, zi, seed);
  const b = hash2(xi + 1, zi, seed);
  const c = hash2(xi, zi + 1, seed);
  const d = hash2(xi + 1, zi + 1, seed);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

export function fbm2(x: number, z: number, seed = 0, octaves = 4): number {
  let amp = 0.5;
  let sum = 0;
  let norm = 0;
  let f = 1;
  for (let o = 0; o < octaves; o++) {
    sum += noise2(x * f, z * f, seed + o * 31) * amp;
    norm += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / norm;
}

/** Distance from p to a polyline. */
export function polylineDistance(p: V2, pts: readonly V2[]): number {
  let best = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const c = closestOnSegment(p, pts[i] as V2, pts[i + 1] as V2);
    best = Math.min(best, dist(p, c));
  }
  return best;
}

/** 1 inside `width/2`, fading to 0 over `soft` metres. */
export function band(d: number, width: number, soft = 1.5): number {
  const h = width / 2;
  if (d <= h) return 1;
  if (d >= h + soft) return 0;
  const t = 1 - (d - h) / soft;
  return t * t * (3 - 2 * t);
}

export interface ScatterOptions {
  count: number;
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
  minDist: number;
  accept?: (p: V2) => boolean;
  maxTries?: number;
}

/** Rejection-sampled scatter with a minimum spacing (Poisson-disk-like). */
export function scatter(rng: Rng, o: ScatterOptions, existing: V2[] = []): V2[] {
  const out: V2[] = [];
  const all = [...existing];
  const tries = o.maxTries ?? o.count * 30;
  for (let i = 0; i < tries && out.length < o.count; i++) {
    const p = { x: rng.range(o.minX, o.maxX), z: rng.range(o.minZ, o.maxZ) };
    if (o.accept && !o.accept(p)) continue;
    if (all.some((q) => dist(p, q) < o.minDist)) continue;
    out.push(p);
    all.push(p);
  }
  return out;
}

/** Positions evenly spaced along a polyline (for fences, walls, lamp rows). */
export function alongPolyline(pts: readonly V2[], spacing: number): { p: V2; angle: number }[] {
  const out: { p: V2; angle: number }[] = [];
  let carry = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i] as V2;
    const b = pts[i + 1] as V2;
    const len = dist(a, b);
    const angle = Math.atan2(b.x - a.x, b.z - a.z);
    let t = carry;
    while (t <= len) {
      const k = t / len;
      out.push({ p: { x: a.x + (b.x - a.x) * k, z: a.z + (b.z - a.z) * k }, angle });
      t += spacing;
    }
    carry = t - len;
  }
  return out;
}

/** Agent radius used to inflate obstacles on the navigation grid. */
export const AGENT_RADIUS = 0.45;

export function buildNav(layout: ZoneLayout, cell = 0.5): NavGrid {
  const nav = new NavGrid(layout.minX, layout.minZ, layout.width, layout.depth, cell);
  const w = layout.walkable;
  if (w?.kind === 'circle') nav.blockOutsideCircle({ x: w.x, z: w.z }, w.r - AGENT_RADIUS);
  else if (w?.kind === 'poly') {
    for (let cz = 0; cz < nav.rows; cz++) {
      for (let cx = 0; cx < nav.cols; cx++) {
        if (!pointInPolygon(nav.centerOf(cx, cz), w.pts)) nav.setCell(cx, cz, true);
      }
    }
  }
  // Keep a margin at the map edge.
  const m = 1.5;
  nav.blockRect({ x: layout.minX + layout.width / 2, z: layout.minZ + m / 2 }, layout.width / 2, m / 2);
  nav.blockRect({ x: layout.minX + layout.width / 2, z: layout.minZ + layout.depth - m / 2 }, layout.width / 2, m / 2);
  nav.blockRect({ x: layout.minX + m / 2, z: layout.minZ + layout.depth / 2 }, m / 2, layout.depth / 2);
  nav.blockRect({ x: layout.minX + layout.width - m / 2, z: layout.minZ + layout.depth / 2 }, m / 2, layout.depth / 2);

  for (const p of layout.props) blockProp(nav, p);
  for (const wr of layout.water) {
    if (!wr.deep) continue;
    const s = wr.shape;
    if (s.kind === 'sheet') {
      for (let cz = 0; cz < nav.rows; cz++) {
        for (let cx = 0; cx < nav.cols; cx++) {
          const c = nav.centerOf(cx, cz);
          if (layout.heightAt(c.x, c.z) < wr.level - 0.35) nav.setCell(cx, cz, true);
        }
      }
    } else if (s.kind === 'rect') nav.blockRect({ x: s.x, z: s.z }, s.w / 2 + AGENT_RADIUS * 0.5, s.d / 2 + AGENT_RADIUS * 0.5);
    else nav.blockCircle({ x: s.x, z: s.z }, s.r + AGENT_RADIUS * 0.5);
  }
  for (const c of layout.blockers.circles) nav.blockCircle({ x: c.x, z: c.z }, c.r + AGENT_RADIUS);
  for (const r of layout.blockers.rects) nav.blockRect({ x: r.x, z: r.z }, r.hw + AGENT_RADIUS, r.hd + AGENT_RADIUS, r.rot ?? 0);
  for (const poly of layout.blockers.polys) nav.blockPolygon(poly);
  return nav;
}

function blockProp(nav: NavGrid, p: PropInstance): void {
  const c = p.collider;
  if (!c) return;
  const s = p.scale ?? 1;
  if ('r' in c) nav.blockCircle({ x: p.x, z: p.z }, c.r * s + AGENT_RADIUS);
  else nav.blockRect({ x: p.x, z: p.z }, (c.w * s) / 2 + AGENT_RADIUS, (c.d * s) / 2 + AGENT_RADIUS, p.rot ?? 0);
}

export function seededRng(zoneId: string, salt = 0): Rng {
  return new Rng(`${zoneId}:${salt}`);
}
